/**
 * NewRunModal — the "New BHA run" wide grouped form (§27). A parent header
 * (run #, in/out MD, hours) + a bit section (size, type, jets, vendor) bound to
 * the Oxbo-M catalogs via CatalogPicker. On save it hands a draft up to the
 * panel, which appends it optimistically — the live POST/bulk-upsert wiring
 * lands with §10.3. Depths/ROP the server computes are never entered here.
 */
import { useState } from "react";
import { WideGroupedModal, FormSection, FormField } from "@components/input";
import { CatalogPicker } from "@components/catalog";
import { useLocale } from "@hooks/useLocale";

export interface NewRunDraft {
  run_no: number | null;
  md_in: number | null;
  bit_size_entry_key: string | null;
  bit_type_entry_key: string | null;
  bit_jets: string | null;
  bit_vendor_entry_key: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (draft: NewRunDraft) => void;
  context?: string;
  /** Report date → catalog effective-date filter. */
  asOf?: string;
  nextRunNo: number;
}

const EMPTY = (nextRunNo: number): NewRunDraft => ({
  run_no: nextRunNo,
  md_in: null,
  bit_size_entry_key: null,
  bit_type_entry_key: null,
  bit_jets: null,
  bit_vendor_entry_key: null,
});

export function NewRunModal({ open, onClose, onSave, context, asOf, nextRunNo }: Props) {
  const { t } = useLocale();
  const [draft, setDraft] = useState<NewRunDraft>(() => EMPTY(nextRunNo));
  const patch = (p: Partial<NewRunDraft>) => setDraft((d) => ({ ...d, ...p }));

  const canSave = draft.run_no != null && draft.md_in != null && draft.bit_size_entry_key != null;

  const save = () => {
    if (!canSave) return;
    onSave(draft);
    setDraft(EMPTY(nextRunNo + 1));
    onClose();
  };

  const numInput = (
    value: number | null,
    on: (v: number | null) => void,
    label: string,
  ) => (
    <input
      type="number"
      value={value ?? ""}
      aria-label={label}
      onChange={(e) => on(e.target.value === "" ? null : Number(e.target.value))}
    />
  );

  return (
    <WideGroupedModal
      open={open}
      onClose={onClose}
      title={t("bha.newRun")}
      context={context}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button type="button" className="btn primary" disabled={!canSave} onClick={save}>
            {t("bha.form.save")}
          </button>
        </>
      }
    >
      <FormSection title={t("bha.runDetail")} three>
        <FormField label={t("bha.form.runNo")} required>
          {numInput(draft.run_no, (v) => patch({ run_no: v }), t("bha.form.runNo"))}
        </FormField>
        <FormField label={t("bha.col.inMd")} required unit="ft" owner="p">
          {numInput(draft.md_in, (v) => patch({ md_in: v }), t("bha.col.inMd"))}
        </FormField>
        <FormField label={t("bha.form.vendor")} owner="m">
          <CatalogPicker
            catalogKey="vendors"
            value={draft.bit_vendor_entry_key}
            onChange={(code) => patch({ bit_vendor_entry_key: code })}
            asOf={asOf}
            placeholder={t("bha.form.vendor")}
            ariaLabel={t("bha.form.vendor")}
          />
        </FormField>
      </FormSection>

      <FormSection title={t("bha.bit")} three>
        <FormField label={t("bha.bit.size")} required owner="m">
          <CatalogPicker
            catalogKey="nominal_sizes"
            value={draft.bit_size_entry_key}
            onChange={(code) => patch({ bit_size_entry_key: code })}
            asOf={asOf}
            placeholder={t("bha.bit.size")}
            ariaLabel={t("bha.bit.size")}
          />
        </FormField>
        <FormField label={t("bha.form.bitType")} owner="m">
          <CatalogPicker
            catalogKey="bit_types"
            value={draft.bit_type_entry_key}
            onChange={(code) => patch({ bit_type_entry_key: code })}
            asOf={asOf}
            placeholder={t("bha.form.bitType")}
            ariaLabel={t("bha.form.bitType")}
          />
        </FormField>
        <FormField label={t("bha.bit.jets")}>
          <input
            type="text"
            value={draft.bit_jets ?? ""}
            aria-label={t("bha.bit.jets")}
            placeholder="14/14/16"
            onChange={(e) => patch({ bit_jets: e.target.value || null })}
          />
        </FormField>
      </FormSection>
    </WideGroupedModal>
  );
}
